import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Routes } from "@angular/router";
import { LocalisationService } from "@apps/patient-core/src/app/services/localisation.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { loadComponentWithErrorHandling } from "@apps/patient-core/src/app/utils/routing";
import { JwtResolver } from "./resolvers/jwt-resolver";
import { BookableApptsService } from "./services/bookable-appts.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { Constants } from "@apps/patient-app/constants";

@Injectable({
  providedIn: "root",
})
export class PatientTypeSelectionAuthGuard {
  constructor(private _bookableApptsService: BookableApptsService, private _navigationService: NavigationService) {}
  canActivate(): boolean {
    if (this._bookableApptsService.shouldSkipNewExisting) {
      this._navigationService.navigate("/login");
      return false;
    }

    return true;
  }
}
@Injectable({
  providedIn: "root",
})
export class RestrictedAuthGuard {
  constructor(private _authService: BaseAuthService, private _navigationService: NavigationService) {}
  canActivate(): boolean {
    if (this._authService.isRestricted) {
      this._navigationService.navigate("/restricted/access");
      return false;
    }

    return true;
  }
}

@Injectable({
  providedIn: "root",
})
export class LoginAuthGuard {
  constructor(private _authService: BaseAuthService, private _navigationService: NavigationService, private _localisationService: LocalisationService) {}
  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (state.url === Constants.ROUTES.LOGIN_RE_ENTER_PASSWORD.path) {
      const isLoggedIn = this._authService.isLoggedIn();
      if (!isLoggedIn) this._navigationService.navigate("/error");
      return isLoggedIn;
    }

    if (this._authService.isLoggedIn()) {
      this._navigationService.navigate("/my-dental");
      return false;
    }

    return true;
  }
}

export const shortCodeRoutes: Routes = [
  {
    path: "r/:short_code",
    data: { appView: true },
    loadComponent: loadComponentWithErrorHandling(() => import("./components/short-code/short-code.component").then((m) => m.ShortCodeComponent)),
  },
  {
    path: "re/:patient_id/:mobile_number",
    loadComponent: loadComponentWithErrorHandling(() =>
      import("./components/recall-appointment-link-handler/recall-appointment-link-handler.component").then((m) => m.RecallAppointmentLinkHandlerComponent)
    ),
  },
];
const bookableAppointmentRoutes: Routes = [
  {
    path: "book",
    loadChildren: () => import("./bookable-appointments/bookable-appointments.routing").then((m) => m.BOOKABLE_APPTS_ROUTES),
    resolve: {
      data: JwtResolver,
    },
    data: {
      appView: true,
    },
  },
];

export const miscRoutes: Routes = [
  // Used to preview the appointment(s) from manage app
  {
    path: "preview-appointment",
    loadComponent: loadComponentWithErrorHandling(() =>
      import("./bookable-appointments/site-appointment-preview/site-appointment-preview.component").then(
        (m) => m.BookableAppointmentSiteAppointmentPreviewComponent
      )
    ),
  },
];

const preferencesRoutes: Routes = [
  {
    path: "preferences",
    loadChildren: () => import("./preferences/preferences.routing").then((m) => m.PREFERENCES_ROUTES),
    resolve: {
      data: JwtResolver,
    },
  },
];

const loginRoutesV4: Routes = [
  {
    path: "login",
    loadChildren: () => import("./components/login-v4/login-v4.routing").then((m) => m.LOGIN_ROUTES),
    canActivate: [LoginAuthGuard],
  },
];

const myDentalRoutes: Routes = [
  {
    path: "my-dental",
    loadChildren: () => import("./my-dental/my-dental.routing").then((m) => m.MY_DENTAL_ROUTES),
  },
  {
    path: "restricted",
    loadChildren: () => import("./restricted/restricted.routing").then((m) => m.RESTRICTED_ROUTES),
  },
];

const unsupportedRoute: Routes = [
  {
    path: "unsupported",
    loadComponent: loadComponentWithErrorHandling(() =>
      import("@apps/patient-core/src/app/unsupported/unsupported/unsupported.component").then((m) => m.UnsupportedComponent)
    ),
  },
];

const noRoute: Routes = [
  {
    path: "",
    loadComponent: loadComponentWithErrorHandling(() =>
      import("./components/patient-type-selection/patient-type-selection.component").then((m) => m.PatientTypeSelectionComponent)
    ),
    resolve: {
      data: JwtResolver,
    },
    canActivate: [PatientTypeSelectionAuthGuard],
  },
  {
    path: "**",
    redirectTo: "/",
  },
];

const signOutRoute: Routes = [
  {
    path: "signout",
    loadComponent: loadComponentWithErrorHandling(() => import("./components/signout/signout.component").then((m) => m.SignoutComponent)),
    resolve: {
      data: JwtResolver,
    },
  },
];

const errorRoutes: Routes = [
  {
    path: "error",
    loadComponent: loadComponentWithErrorHandling(() =>
      import("@apps/patient-core/src/app/default-no-route/default-no-route.component").then((m) => m.DefaultNoRouteComponent)
    ),
  },
];

const brandSiteAliasRoute: Routes = [
  {
    path: "practices/:alias",
    loadComponent: loadComponentWithErrorHandling(() =>
      import("./components/brand-site-alias/brand-site-alias.component").then((m) => m.BrandSiteAliasComponent)
    ),
    children: [
      {
        path: "**",
        loadComponent: loadComponentWithErrorHandling(() =>
          import("./components/brand-site-alias/brand-site-alias.component").then((m) => m.BrandSiteAliasComponent)
        ),
      },
    ],
  },
];

export const APP_ROUTES: Routes = [
  ...miscRoutes,
  ...brandSiteAliasRoute,
  ...bookableAppointmentRoutes,
  ...loginRoutesV4,
  ...myDentalRoutes,
  ...errorRoutes,
  ...shortCodeRoutes,
  ...signOutRoute,
  ...preferencesRoutes,
  ...unsupportedRoute,
  ...noRoute,
].map((route) => {
  if (!route.redirectTo && route.path !== "restricted" && !route.path?.startsWith("r/") && !route.path?.startsWith("re/")) {
    route.canActivate = route.canActivate || [];
    route.canActivate.push(RestrictedAuthGuard);
  }

  return route;
});
